import type { ItemPickerTypeOption } from '@elfsight/configurator-contracts';
import type { CarouselSelectorControlOption } from '@elfsight/configurator-controls';
import { Icon } from '@elfsight/lib-icon';
import type { MacroMessageDescriptor } from '@lingui/core/macro';
import { msg } from '@lingui/core/macro';
import type { PostLayoutType } from '../types';
import type { SourceValueByPlatform } from '../widget/types';

export enum SourceTypes {
  FACEBOOK = 'facebook',
  INSTAGRAM = 'instagram',
  LINKEDIN = 'linkedin',
  TUMBLR = 'tumblr',
  TWITTER = 'twitter',
  PINTEREST = 'pinterest',
  TIKTOK = 'tiktok',
  VIMEO = 'vimeo',
  RSS = 'rss',
  YOUTUBE = 'youtube',
  VKONTAKTE = 'vkontakte'
}

export type SourceId = SourceTypes | `${SourceTypes}`;

export type StandaloneApp =
  | SourceTypes.LINKEDIN
  | SourceTypes.VKONTAKTE
  | SourceTypes.RSS
  | SourceTypes.TIKTOK
  | SourceTypes.TUMBLR;

export type ConnectionTypeOption<P extends SourceId> = ItemPickerTypeOption<
  SourceValueByPlatform<P>['connectionType']
>;

export type SourceMetaValue<P extends SourceId = SourceId> = {
  id: P;
  title: string;
  color: string;
  overlayColor: string;
  icon: string;
  nativeIcon: string;
  configuratorIcon?: string;
  followButtonText?: string;
  gradient?: string;
  connectionTypesGetter?: (
    t: (descriptor: MacroMessageDescriptor) => string
  ) => ConnectionTypeOption<P>[];
  postLayoutOptions?: CarouselSelectorControlOption<PostLayoutType>[];
  verifiedBadgeIcon?: string;
  verifiedBadgeSize?: number;
  isVerifiedBadgeMulticolor?: boolean;
};

const ICONS_URL = 'https://static.elfsight.com/icons';

const CONFIGURATOR_ASSETS_URL =
  'https://static.elfsight.com/configurator/assets/feeds';

const CONNECTION_TYPE_BASE_URL =
  'https://static.elfsight.com/icons/configurator-social-feed-connection-type';

export const facebook: SourceMetaValue<SourceTypes.FACEBOOK> = {
  id: SourceTypes.FACEBOOK,
  title: 'Facebook',
  icon: `${ICONS_URL}/facebook.svg`,
  nativeIcon: `${ICONS_URL}/facebook-multicolor.svg`,
  color: 'rgb(24, 119, 242)',
  overlayColor: 'rgba(24, 119, 242, 0.9)',
  // t('Follow')
  followButtonText: 'Follow'
};

export const instagram: SourceMetaValue<SourceTypes.INSTAGRAM> = {
  id: SourceTypes.INSTAGRAM,
  title: 'Instagram',
  icon: `${ICONS_URL}/instagram.svg`,
  nativeIcon: `${ICONS_URL}/instagram-multicolor.svg`,
  color: 'rgb(208, 0, 110)',
  overlayColor: 'rgba(208, 0, 110, 0.9)',
  followButtonText: 'Follow',
  gradient:
    'linear-gradient(225deg, rgb(245, 202, 23) 0%, rgb(245, 36, 39) 50%, #B900B3 100%)',
  connectionTypesGetter: (t) => {
    return [
      {
        label: t(msg`Account`),
        value: 'instagramAccount',
        icon: () => (
          <ConnectionTypeIcon iconPath="instagram-account-multicolor.svg" />
        )
      },
      {
        label: t(msg`Account`),
        value: 'legacyInstagramPersonalAccount',
        icon: () => (
          <ConnectionTypeIcon iconPath="instagram-account-multicolor.svg" />
        )
      },
      {
        label: t(msg`Account`),
        value: 'legacyInstagramBusinessAccount',
        icon: () => (
          <ConnectionTypeIcon iconPath="instagram-account-multicolor.svg" />
        )
      },
      {
        label: t(msg`Hashtag`),
        value: 'instagramHashtag',
        icon: () => (
          <ConnectionTypeIcon iconPath="instagram-hashtag-multicolor.svg" />
        )
      },
      {
        label: t(msg`Mentions & Tags`),
        value: 'instagramMentions',
        icon: () => (
          <ConnectionTypeIcon iconPath="instagram-mentions-multicolor.svg" />
        )
      }
    ];
  }
};

export const linkedin: SourceMetaValue<SourceTypes.LINKEDIN> = {
  id: SourceTypes.LINKEDIN,
  title: 'LinkedIn',
  icon: `${ICONS_URL}/app-social-feed-sources-linkedin.svg`,
  nativeIcon: `${ICONS_URL}/app-social-feed-sources-linkedin-multicolor.svg`,
  color: 'rgb(10, 102, 194)',
  overlayColor: 'rgba(10, 102, 194, 0.9)',
  followButtonText: 'Follow',
  postLayoutOptions: [
    {
      value: 'card1',
      picture: `${CONFIGURATOR_ASSETS_URL}/linkedin-card-1.png`
    },
    {
      value: 'card2',
      picture: `${CONFIGURATOR_ASSETS_URL}/linkedin-card-2.png`
    },
    {
      value: 'card3',
      picture: `${CONFIGURATOR_ASSETS_URL}/linkedin-card-3.png`
    },
    {
      value: 'card4',
      picture: `${CONFIGURATOR_ASSETS_URL}/linkedin-card-4.png`
    },
    {
      value: 'card5',
      picture: `${CONFIGURATOR_ASSETS_URL}/linkedin-card-5.png`
    }
  ],
  verifiedBadgeIcon: `${ICONS_URL}/app-social-feed-linkedin-verified-icon-multicolor.svg`,
  verifiedBadgeSize: 14,
  isVerifiedBadgeMulticolor: true
};

export const tumblr: SourceMetaValue<SourceTypes.TUMBLR> = {
  id: SourceTypes.TUMBLR,
  title: 'Tumblr',
  icon: `${ICONS_URL}/tumblr.svg`,
  configuratorIcon: `${ICONS_URL}/tumblr-white-multicolor.svg`,
  nativeIcon: `${ICONS_URL}/tumblr-multicolor.svg`,
  color: 'rgba(1, 25, 53)',
  overlayColor: 'rgba(1, 25, 53, 0.9)',
  followButtonText: 'Follow',
  gradient: 'linear-gradient(225deg, #023665, #011935)'
};

export const twitter: SourceMetaValue<SourceTypes.TWITTER> = {
  id: SourceTypes.TWITTER,
  title: 'X',
  icon: `${ICONS_URL}/twitter-x.svg`,
  configuratorIcon: `${ICONS_URL}/twitter-x-white-multicolor.svg`,
  nativeIcon: `${ICONS_URL}/twitter-x-multicolor.svg`,
  color: 'rgba(0, 0, 0)',
  overlayColor: 'rgba(0, 0, 0, 0.9)',
  followButtonText: 'Follow',
  connectionTypesGetter: (t) => [
    {
      label: t(msg`Profile`),
      value: 'twitterProfile',
      icon: () => <ConnectionTypeIcon iconPath="x-profile-multicolor.svg" />
    },
    {
      label: t(msg`Profile`),
      value: 'legacyTwitterProfile',
      icon: () => <ConnectionTypeIcon iconPath="x-profile-multicolor.svg" />
    },
    {
      label: t(msg`Hashtag`),
      value: 'twitterHashtag',
      icon: () => <ConnectionTypeIcon iconPath="x-hashtag-multicolor.svg" />
    },
    {
      label: t(msg`Hashtag`),
      value: 'legacyTwitterHashtag',
      icon: () => <ConnectionTypeIcon iconPath="x-hashtag-multicolor.svg" />
    }
  ]
};

export const pinterest: SourceMetaValue<SourceTypes.PINTEREST> = {
  id: SourceTypes.PINTEREST,
  title: 'Pinterest',
  icon: `${ICONS_URL}/pinterest.svg`,
  nativeIcon: `${ICONS_URL}/pinterest-multicolor.svg`,
  color: 'rgb(230, 0, 35)',
  overlayColor: 'rgba(230, 0, 35, 0.9)',
  followButtonText: 'Follow',
  connectionTypesGetter: (t) => [
    {
      label: t(msg`Profile`),
      value: 'pinterestProfile',
      icon: () => (
        <ConnectionTypeIcon iconPath="pinterest-profile-multicolor.svg" />
      )
    },
    {
      label: t(msg`Board`),
      value: 'pinterestBoard',
      icon: () => (
        <ConnectionTypeIcon iconPath="pinterest-board-multicolor.svg" />
      )
    }
  ]
};

export const tiktok: SourceMetaValue<SourceTypes.TIKTOK> = {
  id: SourceTypes.TIKTOK,
  title: 'TikTok',
  icon: `${ICONS_URL}/tiktok.svg`,
  nativeIcon: `${ICONS_URL}/tiktok-multicolor.svg`,
  color: 'rgb(17, 17, 17)',
  overlayColor: 'rgba(17, 17, 17, 0.9)',
  connectionTypesGetter: (t) => [
    {
      label: t(msg`Profile`),
      value: 'tiktokProfile',
      icon: () => (
        <ConnectionTypeIcon iconPath="tiktok-profile-multicolor.svg" />
      )
    },
    {
      label: t(msg`Hashtag`),
      value: 'tiktokHashtag',
      icon: () => (
        <ConnectionTypeIcon iconPath="tiktok-hashtag-multicolor.svg" />
      )
    }
  ],
  followButtonText: 'Follow'
};

export const vimeo: SourceMetaValue<SourceTypes.VIMEO> = {
  id: SourceTypes.VIMEO,
  title: 'Vimeo',
  icon: `${ICONS_URL}/vimeo.svg`,
  nativeIcon: `${ICONS_URL}/vimeo-multicolor.svg`,
  color: 'rgb(26, 183, 234)',
  overlayColor: 'rgba(26, 183, 234, 0.9)',
  followButtonText: 'Follow',
  connectionTypesGetter: (t) => [
    {
      label: t(msg`Channel`),
      value: 'vimeoChannel',
      icon: () => <ConnectionTypeIcon iconPath="vimeo-channel-multicolor.svg" />
    },
    {
      label: t(msg`Showcase`),
      value: 'vimeoShowcase',
      icon: () => (
        <ConnectionTypeIcon iconPath="vimeo-showcase-multicolor.svg" />
      )
    },
    {
      label: t(msg`Group`),
      value: 'vimeoGroup',
      icon: () => <ConnectionTypeIcon iconPath="vimeo-group-multicolor.svg" />
    },
    {
      label: t(msg`User`),
      value: 'vimeoUser',
      icon: () => <ConnectionTypeIcon iconPath="vimeo-user-multicolor.svg" />
    },
    {
      label: t(msg`Specific Video`),
      value: 'vimeoSpecificVideo',
      icon: () => (
        <ConnectionTypeIcon iconPath="vimeo-specific-video-multicolor.svg" />
      )
    }
  ]
};

export const rss: SourceMetaValue<SourceTypes.RSS> = {
  id: SourceTypes.RSS,
  title: 'RSS',
  icon: `${ICONS_URL}/rss.svg`,
  nativeIcon: `${ICONS_URL}/rss-multicolor.svg`,
  color: 'rgb(255, 173, 29)',
  overlayColor: 'rgba(255, 173, 29, 0.9)',
  // t('Subscribe')
  followButtonText: 'Subscribe'
};

export const youtube: SourceMetaValue<SourceTypes.YOUTUBE> = {
  id: SourceTypes.YOUTUBE,
  title: 'YouTube',
  icon: `${ICONS_URL}/youtube.svg`,
  nativeIcon: `${ICONS_URL}/youtube-multicolor.svg`,
  color: 'rgb(255, 0, 0)',
  overlayColor: 'rgba(255, 0, 0, 0.9)',
  followButtonText: 'Subscribe',
  connectionTypesGetter: (t) => [
    {
      label: t(msg`Channel`),
      value: 'youtubeChannel',
      icon: () => (
        <ConnectionTypeIcon iconPath="youtube-channel-multicolor.svg" />
      )
    },
    {
      label: t(msg`Playlist`),
      value: 'youtubePlaylist',
      icon: () => (
        <ConnectionTypeIcon iconPath="youtube-playlist-multicolor.svg" />
      )
    }
  ]
};

export const vkontakte: SourceMetaValue<SourceTypes.VKONTAKTE> = {
  id: SourceTypes.VKONTAKTE,
  title: 'VK',
  icon: `${ICONS_URL}/vkontakte.svg`,
  nativeIcon: `${ICONS_URL}/vkontakte-multicolor.svg`,
  color: 'rgb(39, 135, 245)',
  overlayColor: 'rgb(39, 135, 245, 0.9)',
  followButtonText: 'Follow',
  gradient: 'linear-gradient(225deg, #58A6FF, #004EEB)',
  connectionTypesGetter: (t) => [
    {
      label: t(msg`Public Page`),
      value: 'vkontaktePublic',
      icon: () => (
        <ConnectionTypeIcon iconPath="vk-public-page-multicolor.svg" />
      )
    },
    {
      label: t(msg`Group`),
      value: 'vkontakteGroup',
      icon: () => <ConnectionTypeIcon iconPath="vk-group-multicolor.svg" />
    },
    {
      label: t(msg`My Profile`),
      value: 'vkontakteOwnProfile',
      icon: () => <ConnectionTypeIcon iconPath="vk-my-profile-multicolor.svg" />
    },
    {
      label: t(msg`Public Profile`),
      value: 'vkontakteProfile',
      icon: () => (
        <ConnectionTypeIcon iconPath="vk-public-profile-multicolor.svg" />
      )
    }
  ]
};

export const sources: {
  [P in SourceTypes]: SourceMetaValue<P>;
} = {
  instagram,
  facebook,
  twitter,
  linkedin,
  tiktok,
  pinterest,
  youtube,
  vimeo,
  tumblr,
  rss,
  vkontakte
};

function ConnectionTypeIcon({ iconPath }: { iconPath: string }) {
  return <Icon size={32} src={`${CONNECTION_TYPE_BASE_URL}-${iconPath}`} />;
}

export function getSourceMetaValue<
  T extends StringWithAutocompleteOptions<SourceId>
>(
  platform: T
): T extends SourceId ? SourceMetaValue : Partial<SourceMetaValue> {
  // @ts-expect-error Есть несоответствия возвращаемого типа, но всё норм
  return sources[platform as SourceId] ?? {};
}
